import { useState, useEffect } from 'react';
import ModelWhereabouts from '../components/ModelWhereabouts';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Comments from '../components/Comments';
import { depthValue, depthNoun } from '../lib/depth';
import { StatStrip, Specs } from '../components/ModelSpecs';

const API = '/api';

function formatBytes(bytes) {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)}B`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)}M`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)}K`;
  return bytes.toString();
}




export default function CheckpointDetail() {
  const { checkpointId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkpoint, setCheckpoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [resolvedLineage, setResolvedLineage] = useState({});
  // Edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editTagline, setEditTagline] = useState('');
  // Profile block (sidecar-owned, editable)
  const [editProfileName, setEditProfileName] = useState('');
  const [editProfileTagline, setEditProfileTagline] = useState('');
  const [editProfileAbout, setEditProfileAbout] = useState('');
  const [editProfileArt, setEditProfileArt] = useState('');
  const [saving, setSaving] = useState(false);
  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Star state
  const [starring, setStarring] = useState(false);
  // Meta download state
  const [downloadingMeta, setDownloadingMeta] = useState(false);
  // Owner tools: visibility and the revision trail
  const [revisions, setRevisions] = useState([]);
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  const authHeaders = () => {
    const h = {};
    const t = localStorage.getItem('logos_session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };

  const canEdit = user && checkpoint && (
    user.user_id === checkpoint.creator_id || user.role === 'admin'
  );

  useEffect(() => {
    fetchCheckpoint();
  }, [checkpointId]);

  useEffect(() => {
    if (canEdit) fetchRevisions();
  }, [canEdit, checkpoint?.head_revision_id]);

  const fetchRevisions = async () => {
    try {
      const res = await fetch(`${API}/checkpoints/${checkpointId}/revisions`, {
        credentials: 'include', headers: authHeaders()
      });
      if (res.ok) setRevisions(await res.json());
    } catch { /* the trail is optional */ }
  };

  // publishing is its own step: a licence first, then public
  const [publishing, setPublishing] = useState(false);
  const [licences, setLicences] = useState([]);
  const [licence, setLicence] = useState('');
  useEffect(() => {
    if (!publishing || licences.length) return;
    fetch(`${API}/licences`).then(r => r.json()).then(list => { setLicences(list); setLicence(l => l || list[0]?.id || ''); });
  }, [publishing, licences.length]);

  const setVisibility = async (visibility, chosenLicence) => {
    if (!checkpoint || visibility === checkpoint.visibility) return;
    if (visibility === 'public' && !checkpoint.licence && !chosenLicence) { setPublishing(true); return; }
    setVisibilitySaving(true);
    try {
      const res = await fetch(`${API}/checkpoints/${checkpointId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify(chosenLicence ? { visibility, licence: chosenLicence } : { visibility }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) { setCheckpoint(prev => ({ ...prev, visibility, licence: body.licence ?? prev.licence })); setPublishing(false); }
      else setError(typeof body.detail === 'string' ? body.detail : 'could not change visibility');
    } catch { setError('could not change visibility'); }
    finally { setVisibilitySaving(false); }
  };

  const togglePin = async (revision) => {
    const res = await fetch(`${API}/checkpoints/${checkpointId}/revisions/${revision.revision_id}/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({ pinned: !revision.pinned }),
    });
    if (res.ok) fetchRevisions();
  };

  // Dynamically update <head> meta tags so deep-links share nicely even
  // when pointed directly at /checkpoints/:id. Bots that don't run JS will
  // still get the static site-level OG; our canonical share URL is /api/share/:id.
  useEffect(() => {
    if (!checkpoint) return;
    const prevTitle = document.title;
    const ogImg = `${window.location.origin}/api/og/checkpoint/${checkpointId}.png`;
    const desc = checkpoint.og_tagline
      || `by ${checkpoint.creator_username} · ${(checkpoint.corpus_tags || []).slice(0, 3).join(', ')}`;
    document.title = `${checkpoint.name} · logOS`;
    const set = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [k, v] = selector.replace(/^meta\[|\]$/g, '').split('=');
        el.setAttribute(k, v.replace(/"/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    set('meta[property="og:title"]', 'content', `${checkpoint.name} · logOS`);
    set('meta[property="og:description"]', 'content', desc);
    set('meta[property="og:image"]', 'content', ogImg);
    set('meta[name="twitter:title"]', 'content', `${checkpoint.name} · logOS`);
    set('meta[name="twitter:description"]', 'content', desc);
    set('meta[name="twitter:image"]', 'content', ogImg);
    return () => { document.title = prevTitle; };
  }, [checkpoint, checkpointId]);

  const fetchCheckpoint = async () => {
    try {
      const response = await fetch(`${API}/checkpoints/${checkpointId}`, {
        credentials: 'include', headers: authHeaders()
      });
      if (!response.ok) throw new Error('checkpoint not found');
      const data = await response.json();
      setCheckpoint(data);
      if (data.checkpoint_history?.length > 0) resolveLineage(data.checkpoint_history);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resolveLineage = async (ids) => {
    try {
      const res = await fetch(`${API}/checkpoints/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkpoint_ids: ids })
      });
      if (res.ok) setResolvedLineage(await res.json());
    } catch {}
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/checkpoints/${checkpointId}/download`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.download_url, '_blank');
        setCheckpoint(prev => prev ? { ...prev, download_count: prev.download_count + 1 } : prev);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadMeta = async () => {
    setDownloadingMeta(true);
    try {
      const res = await fetch(`${API}/checkpoints/${checkpointId}/meta.json`, {
        credentials: 'include', headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.download_url, '_blank');
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'meta download failed');
      }
    } catch (err) {
      console.error('Meta download failed:', err);
    } finally {
      setDownloadingMeta(false);
    }
  };

  const startEditing = () => {
    setEditName(checkpoint.name);
    setEditDescription(checkpoint.description || '');
    setEditTags(checkpoint.corpus_tags.join(', '));
    setEditTagline(checkpoint.og_tagline || '');
    const p = checkpoint.profile || {};
    setEditProfileName(p.name || '');
    setEditProfileTagline(p.tagline || '');
    setEditProfileAbout(p.about || '');
    setEditProfileArt(p.art || '');
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const tags = editTags.split(',').map(t => t.trim()).filter(Boolean);
      if (tags.length === 0) { setError('at least one tag is required'); setSaving(false); return; }
      // Build the profile update. If every field is empty, send null to clear.
      const profileFields = {};
      if (editProfileName.trim()) profileFields.name = editProfileName.trim();
      if (editProfileTagline.trim()) profileFields.tagline = editProfileTagline.trim();
      if (editProfileAbout.trim()) profileFields.about = editProfileAbout.trim();
      if (editProfileArt.trim()) profileFields.art = editProfileArt;  // preserve whitespace exactly
      const profilePayload = Object.keys(profileFields).length ? profileFields : null;

      const res = await fetch(`${API}/checkpoints/${checkpointId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          name: editName.trim(),
          description: editDescription.trim() || null,
          corpus_tags: tags,
          og_tagline: editTagline.trim() || null,
          profile: profilePayload,
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setCheckpoint(prev => ({
          ...prev,
          name: updated.name,
          description: updated.description,
          corpus_tags: updated.corpus_tags,
          og_tagline: updated.og_tagline,
          profile: updated.profile ?? null,
        }));
        setEditing(false);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'save failed');
      }
    } catch { setError('save failed'); }
    finally { setSaving(false); }
  };

  const toggleStar = async () => {
    if (!user || starring) return;
    setStarring(true);
    const prevStarred = checkpoint.starred_by_me;
    const prevCount = checkpoint.star_count || 0;
    // Optimistic update
    setCheckpoint(prev => ({ ...prev, starred_by_me: !prevStarred, star_count: prevCount + (prevStarred ? -1 : 1) }));
    try {
      const res = await fetch(`${API}/checkpoints/${checkpointId}/star`, {
        method: 'POST', headers: authHeaders(), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCheckpoint(prev => ({ ...prev, starred_by_me: data.starred, star_count: data.star_count }));
      } else {
        // Revert
        setCheckpoint(prev => ({ ...prev, starred_by_me: prevStarred, star_count: prevCount }));
      }
    } catch {
      setCheckpoint(prev => ({ ...prev, starred_by_me: prevStarred, star_count: prevCount }));
    } finally { setStarring(false); }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/api/share/${checkpointId}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {}
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/checkpoints/${checkpoint.checkpoint_id}`, {
        method: 'DELETE', headers: authHeaders(), credentials: 'include'
      });
      if (res.ok) navigate('/checkpoints');
      else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'delete failed');
        setConfirmDelete(false);
      }
    } catch { setError('delete failed'); setConfirmDelete(false); }
    finally { setDeleting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-[#888888] lowercase animate-pulse">loading checkpoint...</p>
    </div>
  );

  if (error && !checkpoint) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <p className="text-2xl text-white lowercase mb-4">{error}</p>
        <Link to="/checkpoints" className="text-[#888888] lowercase hover:text-white transition-all duration-300">← back to checkpoints</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black py-12 px-6 lg:px-8 page-wrapper" data-testid="checkpoint-detail-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "SoftwareSourceCode",
        "name": checkpoint.name, "author": { "@type": "Person", "name": checkpoint.creator_username },
        "dateCreated": checkpoint.created_at, "programmingLanguage": "soma",
        "identifier": checkpoint.checkpoint_id, "version": checkpoint.soma_version
      })}} />
      <div className="max-w-4xl mx-auto">
        <Link to="/checkpoints" className="inline-block text-[#888888] lowercase hover:text-white transition-all duration-300 mb-8" data-testid="back-link">
          ← models
        </Link>

        {/* Error banner */}
        {error && <div className="mb-4 p-3 border border-white/20 rounded-lg text-white lowercase text-sm">{error}</div>}

        {/* Name — editable */}
        {editing ? (
          <input
            value={editName}
            onChange={e => setEditName(e.target.value)}
            className="w-full text-4xl lg:text-6xl font-bold text-white lowercase mb-6 bg-transparent border-b border-[#383f38] outline-none focus:border-white transition-colors"
            data-testid="edit-name-input"
          />
        ) : (
          <div className="flex items-start gap-4 mb-6">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-4xl lg:text-6xl font-bold text-white lowercase animate-slide-up break-words" data-testid="checkpoint-name">
                  {(checkpoint.profile && checkpoint.profile.name) || checkpoint.name}
                </h1>
                {checkpoint.runtime_id && (
                  <Link
                    to={`/runtimes/${checkpoint.runtime_id.replace(/^soma\./, '')}`}
                    className="inline-block text-xs font-mono lowercase px-2 py-1 rounded border border-[#22281f] text-[#aaaaaa] hover:border-[#3a413a] hover:text-white transition-colors"
                    title={`this checkpoint targets the ${checkpoint.runtime_id} runtime`}
                    data-testid="checkpoint-runtime-chip"
                  >
                    {checkpoint.runtime_id.replace(/^soma\./, '')}
                  </Link>
                )}
                {checkpoint.visibility && checkpoint.visibility !== 'public' && (
                  <span
                    className="inline-block text-xs font-mono lowercase px-2 py-1 rounded border border-[#3a413a] text-white"
                    title={checkpoint.visibility === 'private' ? 'only you and admins can see this model' : 'anyone with the link can see this model; it is not listed'}
                    data-testid="checkpoint-visibility-chip"
                  >
                    {checkpoint.visibility}
                  </span>
                )}
              </div>
              {checkpoint.profile && checkpoint.profile.tagline && (
                <p className="text-[#aaaaaa] lowercase text-lg mt-2 animate-slide-up" data-testid="checkpoint-tagline" style={{ animationDelay: '50ms' }}>
                  {checkpoint.profile.tagline}
                </p>
              )}
            </div>
            {canEdit && !editing && (
              <button onClick={startEditing} className="mt-2 text-[#555555] lowercase text-sm hover:text-white transition-all duration-300 shrink-0" data-testid="edit-btn">
                edit
              </button>
            )}
          </div>
        )}

        {/* Creator + stars/share/downloads */}
        <div className="flex items-center justify-between mb-8 animate-slide-up gap-3 flex-wrap" style={{ animationDelay: '100ms' }}>
          <div className="flex items-center space-x-3">
            <Link to={`/u/${checkpoint.creator_username}`} className="text-[#888888] hover:text-white transition-all duration-300 lowercase" data-testid="creator-link">
              {checkpoint.creator_username}
            </Link>
            {checkpoint.creator_verified && <img src="/phi-logo-64.png" alt="verified" title="verified" className="verified-badge" />}
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <button onClick={handleDownload} disabled={downloading}
              className="bg-white text-black border border-white px-4 py-1.5 rounded-full font-mono text-sm lowercase hover:bg-transparent hover:text-white transition-all duration-300 disabled:opacity-50"
              data-testid="download-btn">
              {downloading ? 'preparing…' : 'download'}
            </button>
            <button
              onClick={toggleStar}
              disabled={!user || starring}
              title={user ? (checkpoint.starred_by_me ? 'unstar' : 'star') : 'login to star'}
              className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-full font-mono text-sm lowercase transition-all duration-300 ${
                checkpoint.starred_by_me
                  ? 'border-white text-white bg-white/10'
                  : 'border-[#2f362f] text-[#aaaaaa] hover:border-white hover:text-white'
              } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="star-btn"
            >
              <span>{checkpoint.starred_by_me ? '★' : '☆'}</span>
              <span className="tabular-nums">{checkpoint.star_count || 0}</span>
            </button>
            <button
              onClick={copyShareLink}
              className="border border-[#2f362f] text-[#aaaaaa] hover:border-white hover:text-white px-3 py-1.5 rounded-full font-mono text-sm lowercase transition-all duration-300"
              data-testid="share-btn"
            >
              {shareCopied ? 'copied' : 'share'}
            </button>
            {checkpoint.download_count > 0 && (
              <span className="text-[#555555] font-mono text-sm lowercase">
                {checkpoint.download_count.toLocaleString()} download{checkpoint.download_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <div className="mb-10 animate-slide-up" style={{ animationDelay: '150ms' }}>
          <StatStrip checkpoint={checkpoint} />
          {checkpoint.meta_r2_key && (
            <button onClick={handleDownloadMeta} disabled={downloadingMeta}
              className="mt-2 font-mono text-xs text-[#555555] hover:text-white lowercase transition-colors" data-testid="download-meta-btn">
              {downloadingMeta ? 'preparing…' : 'download its .meta.json'}
            </button>
          )}
        </div>

        {/* Metadata Block */}
        <div className="space-y-8 mb-12 animate-slide-up" style={{ animationDelay: '200ms' }}>
          {/* Tags — editable */}
          <div>
            <label className="block font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">tags</label>
            {editing ? (
              <input
                value={editTags}
                onChange={e => setEditTags(e.target.value)}
                placeholder="comma-separated tags"
                className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase outline-none focus:border-white transition-colors"
                data-testid="edit-tags-input"
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {checkpoint.corpus_tags.map(tag => (
                  <span key={tag} className="tag-chip">{tag}</span>
                ))}
              </div>
            )}
          </div>

          {!editing && <Specs checkpoint={checkpoint} />}

          {/* Description — editable */}
          <div>
            <label className="block font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">description</label>
            {editing ? (
              <textarea
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                rows={3}
                className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase outline-none focus:border-white transition-colors resize-none"
                data-testid="edit-description-input"
              />
            ) : (
              checkpoint.description ? (
                <p className="text-[#cccccc] lowercase leading-relaxed">{checkpoint.description}</p>
              ) : (
                <p className="text-[#444444] lowercase italic">no description</p>
              )
            )}
          </div>

          {/* Sidecar profile — art + about (read view) */}
          {!editing && checkpoint.profile && (checkpoint.profile.art || checkpoint.profile.about) && (
            <div className="space-y-6 pt-2 border-t border-[#1e251f]" data-testid="checkpoint-profile">
              {checkpoint.profile.art && (
                <div>
                  <label className="block text-[#666666] lowercase text-sm mb-2 font-mono">art</label>
                  <pre
                    className="text-[#dddddd] font-mono text-xs sm:text-sm leading-tight whitespace-pre overflow-x-auto p-4 border border-[#1a201b] rounded-lg bg-[#0a0e0b]"
                    data-testid="profile-art"
                  >{checkpoint.profile.art}</pre>
                </div>
              )}
              {checkpoint.profile.about && (
                <div>
                  <label className="block text-[#666666] lowercase text-sm mb-2">about</label>
                  <p
                    className="text-[#cccccc] lowercase leading-relaxed whitespace-pre-wrap"
                    data-testid="profile-about"
                  >{checkpoint.profile.about}</p>
                </div>
              )}
            </div>
          )}

          {/* Sidecar profile — editable fields */}
          {editing && (
            <div className="space-y-4 pt-4 border-t border-[#1e251f]">
              <div>
                <label className="block text-[#666666] lowercase text-sm mb-1">
                  profile · name <span className="text-[#333333]">(sidecar override for the model name)</span>
                </label>
                <input
                  type="text"
                  value={editProfileName}
                  onChange={e => setEditProfileName(e.target.value.slice(0, 200))}
                  placeholder="checkpoint name shown at the top"
                  className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase outline-none focus:border-white transition-colors"
                  data-testid="edit-profile-name-input"
                />
              </div>
              <div>
                <label className="block text-[#666666] lowercase text-sm mb-1">
                  profile · tagline <span className="text-[#333333]">(one line under the name)</span>
                </label>
                <input
                  type="text"
                  value={editProfileTagline}
                  onChange={e => setEditProfileTagline(e.target.value.slice(0, 200))}
                  placeholder="local continual language model, trained on ..."
                  className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase outline-none focus:border-white transition-colors"
                  data-testid="edit-profile-tagline-input"
                />
              </div>
              <div>
                <label className="block text-[#666666] lowercase text-sm mb-1">
                  profile · about <span className="text-[#333333]">(editable checkpoint notes)</span>
                </label>
                <textarea
                  value={editProfileAbout}
                  onChange={e => setEditProfileAbout(e.target.value.slice(0, 4000))}
                  rows={4}
                  placeholder="what did you train this on? where does it shine?"
                  className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase outline-none focus:border-white transition-colors resize-none"
                  data-testid="edit-profile-about-input"
                />
              </div>
              <div>
                <label className="block text-[#666666] lowercase text-sm mb-1 font-mono">
                  profile · art <span className="text-[#333333] normal-case">(optional monospace block · preserves whitespace)</span>
                </label>
                <textarea
                  value={editProfileArt}
                  onChange={e => setEditProfileArt(e.target.value.slice(0, 6000))}
                  rows={6}
                  placeholder={`_       _\n \\ (o) /\n  \\_v_/`}
                  className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white font-mono text-sm outline-none focus:border-white transition-colors resize-none whitespace-pre"
                  data-testid="edit-profile-art-input"
                  spellCheck={false}
                />
              </div>
            </div>
          )}

          {/* OG tagline — only when editing. Single line that appears on the share card. */}
          {editing && (
            <div>
              <label className="block text-[#666666] lowercase text-sm mb-2">
                share-card tagline <span className="text-[#333333]">(optional · 100 chars · overrides default)</span>
              </label>
              <input
                type="text"
                value={editTagline}
                onChange={e => setEditTagline(e.target.value.slice(0, 100))}
                placeholder="one line that shows on the social card"
                className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase outline-none focus:border-white transition-colors"
                data-testid="edit-tagline-input"
              />
              <p className="text-[#333333] font-mono text-xs mt-1">
                preview: <a href={`/api/og/checkpoint/${checkpointId}.png`} target="_blank" rel="noreferrer" className="hover:text-white">view card →</a>
              </p>
            </div>
          )}

          {/* Save/Cancel edit buttons */}
          {editing && (
            <div className="flex gap-3 pt-2">
              <button
                onClick={saveEdit}
                disabled={saving}
                className="border border-[#383f38] px-6 py-2 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-lg disabled:opacity-50"
                data-testid="save-edit-btn"
              >
                {saving ? 'saving...' : 'save changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-[#555555] lowercase hover:text-white transition-all duration-300 px-4 py-2"
                data-testid="cancel-edit-btn"
              >
                cancel
              </button>
            </div>
          )}
        </div>

        {/* Lineage Section */}
        <div className="border-t border-[#1e251f] pt-8 mb-10 animate-slide-up" style={{ animationDelay: '300ms' }}>
          <h2 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-4">lineage</h2>
          <div className="mb-4">
            <label className="block font-mono text-xs text-[#777777] lowercase mb-2">model id</label>
            <div className="flex items-center space-x-3">
              <code className="text-white font-mono text-sm bg-[#151b17] px-3 py-1 rounded-md">{checkpoint.checkpoint_id.slice(0, 12)}...</code>
              <button onClick={() => { navigator.clipboard.writeText(checkpoint.checkpoint_id); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="text-[#888888] lowercase hover:text-white transition-all duration-300 text-sm" data-testid="copy-id-btn">
                {copied ? 'copied!' : 'copy full id'}
              </button>
            </div>
          </div>
          <div>
            <p className="text-[#888888] lowercase mb-2">
              {checkpoint.checkpoint_history.length} checkpoint{checkpoint.checkpoint_history.length !== 1 ? 's' : ''} in history
            </p>
            {checkpoint.checkpoint_history.length > 0 && (
              <>
                <button onClick={() => setHistoryExpanded(!historyExpanded)}
                  className="text-[#888888] lowercase hover:text-white transition-all duration-300 text-sm" data-testid="expand-history-btn">
                  {historyExpanded ? 'collapse history' : 'expand to view full history'}
                </button>
                {historyExpanded && (
                  <div className="mt-4 space-y-2 animate-slide-down">
                    {checkpoint.checkpoint_history.map((id, index) => (
                      <div key={id} className="flex items-center space-x-3">
                        <span className="text-[#555555] text-sm">{index + 1}.</span>
                        {resolvedLineage[id] ? (
                          <Link to={`/checkpoints/${id}`} className="text-white hover:text-[#888888] transition-colors text-sm lowercase">
                            {resolvedLineage[id].name} <span className="text-[#555555]">by {resolvedLineage[id].creator_username}</span>
                          </Link>
                        ) : (
                          <code className="text-white font-mono text-sm bg-[#151b17] px-2 py-0.5 rounded" title={id}>{id.slice(0, 12)}...</code>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Update Checkpoint — big fat button for creator */}
        {canEdit && (
          <Link
            to="/upload"
            className="inline-block mt-2 border border-[#383f38] px-5 py-2 text-white text-sm lowercase text-center hover:bg-white hover:text-black transition-all duration-300 rounded-full font-mono"
            data-testid="update-checkpoint-btn"
          >
            upload a new version
          </Link>
        )}

        {/* Visibility and revisions — the owner's tools */}
        {canEdit && (
          <div className="mt-12 pt-8 border-t border-[#1e251f] space-y-8" data-testid="owner-tools">
            {checkpoint.creator_id === user?.user_id && <ModelWhereabouts checkpointId={checkpointId} />}
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-[#888888] lowercase text-sm w-24">visibility</span>
              {['private', 'unlisted', 'public'].map(v => (
                <button key={v} onClick={() => setVisibility(v)} disabled={visibilitySaving}
                  className={`border px-4 py-1.5 rounded-full font-mono text-sm lowercase transition-all duration-300 disabled:opacity-50 ${
                    (checkpoint.visibility || 'private') === v
                      ? 'border-white text-white bg-white/10'
                      : 'border-[#383f38] text-[#888888] hover:text-white hover:border-[#888888]'
                  }`}
                  data-testid={`visibility-${v}`}
                >
                  {v}
                </button>
              ))}
              <p className="basis-full font-mono text-xs text-[#666666] lowercase sm:pl-28">
                {{ private: 'only you. it lives in your workspace.', unlisted: 'anyone with the link; never listed or searchable.',
                   public: `published: listed, searchable, on your profile${checkpoint.licence ? ` · ${checkpoint.licence}` : ''}.` }[checkpoint.visibility || 'private']}
              </p>
              {publishing && (
                <div className="basis-full sm:pl-28 space-y-3 border-l border-[#2a312b] pl-4 py-1" data-testid="publish-step">
                  <p className="text-white text-sm lowercase">publish {checkpoint.name}</p>
                  <p className="text-[#888888] text-sm">
                    once it's public, anyone can find it, download it and keep training it. downloaded copies can't be called back.
                  </p>
                  {(checkpoint.data || []).length > 0 && (
                    <p className="font-mono text-xs text-[#888888] lowercase">
                      it read: {checkpoint.data.map(d => `${d.label} (${d.licence})`).join(', ')}.
                      {checkpoint.data.some(d => d.licence === 'yours' || !d.licence) ? ' it read your own sources: check you can share what it learned from them.' : ''}
                    </p>
                  )}
                  <label className="flex items-center gap-3 font-mono text-xs text-[#888888] lowercase">
                    licence
                    <select value={licence} onChange={e => setLicence(e.target.value)} data-testid="publish-licence"
                      className="bg-transparent border border-[#383f38] rounded-lg px-2 py-1.5 text-white outline-none focus:border-white max-w-xs">
                      {licences.map(l => <option key={l.id} value={l.id} className="bg-black">{l.label}</option>)}
                    </select>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => setVisibility('public', licence)} disabled={!licence || visibilitySaving} data-testid="publish-confirm"
                      className="border border-white bg-white text-black px-5 py-1.5 rounded-full font-mono text-xs lowercase hover:bg-transparent hover:text-white transition-all disabled:opacity-40">
                      publish
                    </button>
                    <button onClick={() => setPublishing(false)} className="font-mono text-xs text-[#888888] hover:text-white lowercase px-3">not now</button>
                  </div>
                </div>
              )}
            </div>
            {revisions.length > 0 && (
              <div>
                <p className="text-[#888888] lowercase text-sm mb-3">
                  revisions · the head and the two before it are kept, plus any you pin
                </p>
                <div className="space-y-2 font-mono text-sm">
                  {revisions.map(r => (
                    <div key={r.revision_id} className="flex items-center gap-4 flex-wrap text-[#aaaaaa]" data-testid="revision-row">
                      <span className={r.head ? 'text-white' : ''}>{r.revision_id.slice(0, 12)}</span>
                      <span className="text-[#555555]">{formatBytes(r.bytes_seen)} seen</span>
                      <span className="text-[#555555]">{r.runtime_version || ''}</span>
                      <span className="text-[#555555]">{new Date(r.created_at).toLocaleString()}</span>
                      {r.head && <span className="text-white lowercase">head</span>}
                      <button onClick={() => togglePin(r)}
                        className="lowercase text-[#555555] hover:text-white transition-colors">
                        {r.pinned ? 'unpin' : 'pin'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete — visible to creator or admin */}
        {canEdit && (
          <div className="mt-12 pt-8 border-t border-[#1e251f]">
            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)}
                className="text-[#555555] lowercase text-sm hover:text-white transition-all duration-300" data-testid="delete-btn">
                delete this checkpoint
              </button>
            ) : (
              <div className="flex items-center gap-4">
                <p className="text-[#888888] lowercase text-sm">are you sure? this cannot be undone.</p>
                <button onClick={handleDelete} disabled={deleting}
                  className="border border-white/20 px-4 py-2 text-white lowercase text-sm hover:bg-white hover:text-black transition-all duration-300 rounded-lg disabled:opacity-50" data-testid="confirm-delete-btn">
                  {deleting ? 'deleting...' : 'yes, delete'}
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  className="text-[#555555] lowercase text-sm hover:text-white transition-all duration-300" data-testid="cancel-delete-btn">
                  cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Comments */}
        <Comments checkpointId={checkpoint.checkpoint_id} />
      </div>
    </div>
  );
}
